import {getReasonText} from '@Libraries/font-load-error.library';

export interface FontFaceLoadSteps {
  /** Settles once the family's stylesheet has been parsed, so its @font-face rules exist; rejects if it failed to load. */
  waitForStyleSheet: () => Promise<void>;
  /** Starts the browser's own load of the family's faces and resolves with the faces that matched. */
  loadFaces: () => Promise<ArrayLike<unknown>>;
}

export const FONT_LOAD_TICK_MS = 1000;
export const FONT_LOAD_RESPONSIVE_TIMEOUT_TICKS = 60;

/**
 * Loads one font family and resolves when the browser reports its faces loaded.
 *
 * The budget is counted in one-second ticks rather than wall-clock time. A tick only elapses while
 * the page is responsive, so a main thread blocked by poster initialisation, or a tab the browser has
 * frozen or throttled, consumes at most one tick however long it lasted; the font then loads on resume
 * instead of being refused for a timeout that ran out while nothing could run.
 */
export const loadFontFace = (fontFamily: string, steps: FontFaceLoadSteps): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const cancelBudget = startResponsiveBudget(() => {
      reject(new Error(`"${fontFamily}" did not load within ${FONT_LOAD_RESPONSIVE_TIMEOUT_TICKS}s of responsive page time`));
    });

    steps
      .waitForStyleSheet()
      .then(() => steps.loadFaces())
      .then((faces) => {
        if (faces.length === 0) {
          throw new Error(`no @font-face rule declares "${fontFamily}"`);
        }
        resolve();
      })
      .catch((reason: unknown) => {
        reject(toError(reason));
      })
      .finally(cancelBudget);
  });
};

/** Each tick is armed only when the previous one has run, so a long stall advances the budget by one tick, not by its length. */
const startResponsiveBudget = (onExhausted: () => void): (() => void) => {
  let ticksElapsed = 0;
  let tickTimer: ReturnType<typeof setTimeout>;
  const onTick = (): void => {
    ticksElapsed += 1;
    if (ticksElapsed >= FONT_LOAD_RESPONSIVE_TIMEOUT_TICKS) {
      onExhausted();
      return;
    }
    tickTimer = setTimeout(onTick, FONT_LOAD_TICK_MS);
  };
  tickTimer = setTimeout(onTick, FONT_LOAD_TICK_MS);
  return (): void => {
    clearTimeout(tickTimer);
  };
};

const toError = (reason: unknown): Error => {
  return reason instanceof Error ? reason : new Error(getReasonText(reason));
};
